import { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) {
    const from = location.state?.from || "/";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      const from = location.state?.from || "/";
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-[#F2E9DB] px-6"
      style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
    >
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-10">
          <span className="inline-block w-2 h-7 bg-[#FFD100]" />
          <span className="text-[#141B4D] font-bold tracking-tight text-lg">
            Indie Law · Content Engine
          </span>
        </div>

        <div className="bg-white border border-[#141B4D]/15 rounded-sm p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-[#141B4D]/5 p-2 rounded-sm">
              <Lock size={16} className="text-[#141B4D]" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold text-[#141B4D] tracking-tight">
              Sign in
            </h1>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
                placeholder="hello@indielaw.com"
                className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Password
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#141B4D] text-[#F2E9DB] py-2.5 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        <p className="text-xs text-[#141B4D]/55 mt-6 text-center">
          Trademark law, translated for entrepreneurs.
        </p>
      </div>
    </div>
  );
}
